import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "./cityPricing";

const nice: ChauffeurPage = {
  slug: "nice",
  title: "VTC & chauffeur privé à Nice",
  subtitle:
    "Chauffeur privé à Nice : aéroport Côte d'Azur, Promenade des Anglais, congrès et liaisons Riviera.",
  keywords: [
    "vtc nice",
    "chauffeur privé nice",
    "chauffeur aéroport nice",
    "vtc promenade des anglais",
  ],
  hero: {
    title: "VTC & chauffeur privé à Nice",
    subtitle:
      "Aéroport Nice Côte d'Azur (T1/T2), Vieux-Nice, Port et déplacements business — suivi de vol, 24h/24.",
    keywords: [
      "VTC Nice",
      "chauffeur Nice aéroport",
      "chauffeur Promenade des Anglais",
      "liaison Nice Monaco",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Nice",
    badges: ["Aéroport T1 & T2", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Nice",
    items: [
      {
        title: "Aéroport sans friction",
        description:
          "2e aéroport de France : accueil pancarte aux Terminaux 1 et 2 et suivi de vol.",
      },
      {
        title: "Connaissance du terrain",
        description:
          "Promenade des Anglais, ruelles du Vieux-Nice, corniches et Port en heure de pointe.",
      },
      {
        title: "Congrès & affaires",
        description:
          "Acropolis, palais des expositions, hôtels du centre : ponctualité business.",
      },
      {
        title: "Liaisons Riviera",
        description:
          "Hops réguliers Nice ↔ Monaco et Nice ↔ Cannes par l'A8 ou les corniches.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Nice",
    services: [
      {
        title: "Transferts aéroport",
        text: "Nice Côte d'Azur (T1/T2) vers le centre, les hôtels ou toute la Côte, avec suivi de vol.",
      },
      {
        title: "Mise à disposition",
        text: "À l'heure ou à la journée pour rendez-vous, shopping et dîners niçois.",
      },
      {
        title: "Congrès & événements",
        text: "Navettes vers le palais Acropolis et les centres de congrès, coordination groupes.",
      },
      {
        title: "Terminal croisières",
        text: "Prise en charge au port de Nice pour escales et embarquements, bagages assistés.",
      },
      {
        title: "Soirées & retours",
        text: "Restaurants du Vieux-Nice, casinos et événements, retour de nuit en sécurité.",
      },
    ],
  },
  locations: {
    heading: "Quartiers & points de prise en charge",
    items: [
      {
        name: "Aéroport Nice Côte d'Azur",
        description: "Terminaux 1 et 2, aviation d'affaires.",
      },
      {
        name: "Promenade des Anglais",
        description: "Palaces et hôtels en front de mer.",
      },
      {
        name: "Vieux-Nice",
        description: "Cours Saleya, ruelles piétonnes, accès rapproché.",
      },
      {
        name: "Carré d'Or & Jean-Médecin",
        description: "Quartier commerçant et hôtels du centre.",
      },
      {
        name: "Port de Nice",
        description: "Bassin Lympia et terminal croisières.",
      },
      {
        name: "Cimiez & Acropolis",
        description: "Quartier résidentiel et palais des congrès.",
      },
    ],
  },
  generic: {
    heading: "Nice, ville-carrefour de la Côte d'Azur",
    subheading:
      "L'aéroport, le centre et les corniches reliés par un même chauffeur.",
    paragraphs: [
      "Nice concentre l'aéroport international, un centre dense et un port de croisières : autant de points où le stationnement et la circulation se compliquent en saison. Un chauffeur privé absorbe ces contraintes — dépose au plus près, attente gérée, pas de recherche de place.",
      "Entre la basse corniche, la moyenne corniche et l'A8, l'itinéraire optimal vers Monaco, Èze ou Cap-Ferrat dépend de l'heure et du trafic. Nos chauffeurs arbitrent en direct pour tenir vos horaires.",
      "Pour les congrès au palais Acropolis comme pour une journée Promenade des Anglais–Vieux-Nice, la mise à disposition garde le même véhicule à votre disposition d'un rendez-vous à l'autre.",
    ],
    bullets: ["Suivi de vol T1/T2", "Eau & chargeurs", "Itinéraire corniches"],
  },
  process: {
    heading: "Réserver un chauffeur à Nice",
    steps: [
      "Indiquez votre vol ou vos adresses (aéroport, hôtel, congrès, port).",
      "Nous calons l'heure de prise en charge et le terminal selon votre compagnie.",
      "Le chauffeur suit le vol et ajuste en cas de retard.",
      "Trajet direct ou mise à disposition selon votre programme de la journée.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Nice"),
    {
      q: "Récupérez-vous aux Terminaux 1 et 2 de l'aéroport de Nice ?",
      a: "Oui. Nous assurons l'accueil aux deux terminaux ainsi qu'à l'aviation d'affaires, avec accueil pancarte et suivi de votre numéro de vol.",
    },
    {
      q: "Combien de temps pour rejoindre Monaco depuis Nice ?",
      a: "Comptez environ 30 à 45 minutes selon l'itinéraire (basse corniche ou A8) et le trafic. Le chauffeur choisit la route la plus rapide au moment du départ.",
    },
    {
      q: "Pouvez-vous gérer une escale au terminal croisières de Nice ?",
      a: "Oui, nous prenons en charge au port de Nice pour les embarquements et débarquements, avec aide aux bagages et coordination des horaires d'escale.",
    },
    {
      q: "Le centre de Nice est-il accessible en véhicule pendant les pics touristiques ?",
      a: "Le stationnement y est difficile en saison ; le chauffeur dépose au plus près de votre adresse et reste joignable, ce qui évite toute recherche de place.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale de l'aéroport et des congrès niçois.",
    },
    {
      q: "Quels véhicules proposez-vous à Nice ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places pour les familles et les bagages, SUV et minibus VIP pour les groupes et les congrès.",
    },
    {
      q: "Faites-vous la longue distance depuis Nice ?",
      a: "Oui, trajets toute distance depuis Nice : Saint-Tropez (env. 1h30 à 2h), Cannes, Monaco, l'Italie voisine (Vintimille, Sanremo, Gênes) et les stations de ski.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un transfert Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Réserver un transfert Monaco Aéroport de Nice",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    { label: "Transferts avec chauffeur à Monaco", href: "/chauffeur/monaco" },
    {
      label: "Voitures à louer à Nice",
      href: "/location/location-voiture-nice",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Nice",
    text: "Aéroport, congrès ou liaison Riviera : réservez votre trajet à Nice.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé à Nice 24h/24 — VTC aéroport & Riviera",
    description:
      "Chauffeur privé et VTC à Nice : aéroport Côte d'Azur (T1/T2) avec suivi de vol, Promenade des Anglais, congrès et liaisons Riviera. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/nice",
  },
};

export default nice;
